import { Injectable, NgZone, inject } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { Observable, of, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { CONSUMER_TYPE, ConsumerType } from '../../domain/consumer-type.domain';
import { ConfigurationService } from '../configuration/configuration.service';
import { WINDOW_TOKEN } from '../window.token';

export interface BrowserWindow extends Window {
  webViewRedirection: {
    postMessage(message: WebViewMessage | string): void;
  };
  // native webview mess - merging  into Window interface
  webkit: {
    messageHandlers: {
      configurationReady: {
        postMessage(message: string): void;
      };
      webViewEvent: {
        postMessage(message: WebViewMessage): void;
      };
    };
  };
  ugobridge: {
    configurationReady?(message: string): void;
    webViewEvent?(message: WebViewMessage): void;
    isETR?(): boolean;
    navigateToNativeEasyTrade?(paramsString: string): void;
  };
  callbackFunction: (message: string) => void;
  isETR?(): boolean;
  navigateToNativeEasyTrade?(paramsString: string): void;
}

export enum WebViewCallbackMessageType {
  openBankingInfo = 'openBankingInfo'
}

export interface WebViewCallbackMessage {
  messageType: WebViewCallbackMessageType,
  payload: unknown
}

export interface WebViewMessage {
  messageType:
  'openBankingFlow' |
  'configuration' |
  'messageCount' |
  'launchMySpend' |
  'easyApplySSO' |
  'omnidial';
  payload: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class InitWebviewService {
  private readonly browserWindow = inject<BrowserWindow>(WINDOW_TOKEN);
  private readonly configurationService = inject<ConfigurationService>(ConfigurationService);
  private readonly consumerType = inject<ConsumerType>(CONSUMER_TYPE);
  private readonly platform = inject<Platform>(Platform);
  private readonly ngZone = inject(NgZone);

  private callbackSubject: Subject<WebViewCallbackMessage> = new Subject<WebViewCallbackMessage>();

  init(): void {
    if (this.platform.IOS) {
      // Due to a problem on WKWebView for iOS, after ensighten (analytics) scripts load they create
      // an iframe element on the DOM and that is causing the window object interface to be set as null
      // immediately after load. If there is any navigation on the site the issue is fixed, but when the
      // flyout menu is clicked on that moment, no messages are sent.
      // TODO Currently 02-Aug-2023 we are on xcode 14.1/iOS 16.1, but monitor future versions to see if this is fixed
      this.browserWindow.webViewRedirection = this.browserWindow.webkit?.messageHandlers?.webViewEvent;
    }

    // Native team will callback window.callbackFunction(...)
    this.browserWindow.callbackFunction = this.callbackMessage.bind(this)

    if (this.consumerType.isEmbedded) {
      this.postMessage({ messageType: 'configuration', payload: this.configurationService.getAll() });

      // TODO: remove when mobile migrates to the new api
      if (this.browserWindow?.webkit?.messageHandlers?.configurationReady?.postMessage !== undefined) {
        this.browserWindow.webkit.messageHandlers.configurationReady.postMessage(
          JSON.stringify(this.configurationService.getAll())
        );
      } else if (this.browserWindow?.ugobridge?.configurationReady !== undefined) {
        this.browserWindow?.ugobridge?.configurationReady(
          JSON.stringify(this.configurationService.getAll())
        );
      }
      // end of TODO
    }
  }

  postEmbeddedMessage(message: WebViewMessage): void {
    if (this.consumerType.isEmbedded) {
      this.postMessage(message);
    }
  }

  // eslint-disable-next-line complexity
  postMessage(message: WebViewMessage): void {
    const callbackExists = this.browserWindow?.webkit?.messageHandlers?.webViewEvent?.postMessage !== undefined ||
      this.browserWindow?.ugobridge?.webViewEvent !== undefined;

    if (callbackExists) {
      let callback;
      if (this.browserWindow?.webkit?.messageHandlers?.webViewEvent?.postMessage !== undefined) {
        callback = (messageToPost: WebViewMessage): void => {
          // eslint-disable-next-line no-console
          console.log('postWebviewMessage:webViewEvent.postMessage');
          this.browserWindow?.webkit?.messageHandlers?.webViewEvent?.postMessage(messageToPost);
        };
      } else if (this.browserWindow?.ugobridge?.webViewEvent !== undefined) {
        callback = (messageToPost: WebViewMessage): void => {
          // eslint-disable-next-line no-console
          console.log('postWebviewMessage:webViewEvent');
          this.browserWindow?.ugobridge?.webViewEvent(messageToPost);
        };
      }
      callback(JSON.stringify(message));
    } else {
      // Hack
      if (this.platform.IOS && this.browserWindow?.webViewRedirection !== undefined) {
        // eslint-disable-next-line no-console
        console.log('postWebviewMessage:webViewRedirection');
        this.browserWindow?.webViewRedirection?.postMessage(JSON.stringify(message));
      }
    }
  }

  callbackMessage(message: string): void {
    this.ngZone.run(() => {
      const callbackMessage: WebViewCallbackMessage = JSON.parse(message) as WebViewCallbackMessage;
      if (callbackMessage) {
        this.callbackSubject.next(callbackMessage);
      }
    })
  }

  onCallbackMessage(callbackMessageType: WebViewCallbackMessageType): Observable<unknown> {
    return this.callbackSubject.pipe(
      filter((message : WebViewCallbackMessage) => message.messageType === callbackMessageType),
      switchMap((message: WebViewCallbackMessage) => of(message.payload))
    );
  }
}
